import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
} from "../../../lib/session";
import type { SessionAdmin } from "../types/auth.types";

/**
 * The admin session, held in the browser.
 *
 * There is no Clerk in this panel and deliberately so: the dietitian app signs
 * in members of the public through a hosted identity provider, and this one
 * signs in a handful of Naurist staff against the admin API's own
 * password + one-time-code pair. One less party holding a credential that can
 * read every customer record.
 *
 * A session lasts until it is ended by hand, at the sign-out button — there is
 * no timer counting it down. The access token underneath it is short-lived,
 * but that is invisible from here: the API client refreshes it on demand (see
 * `lib/api/client.ts`) and only gives up when the refresh token itself is no
 * longer good, at which point the next request simply fails as any other
 * would. What is kept in `localStorage` rather than `sessionStorage` is the
 * admin, the address, and the two tokens — the whole of what the panel needs
 * between page loads and what the API client needs to authenticate a request.
 */
export type AdminSession = ReturnType<typeof readStoredSession>;

export class AuthUtils {
  /**
   * Starts a session, once the six digits have been accepted.
   *
   * Takes the whole admin the answer carried rather than fields picked off it,
   * because that answer is the only place the panel is ever told who is
   * signing in — see `StoredSession.name`. `is_active` is not kept: a session
   * only exists for an admin the server let in.
   */
  static createSession(
    admin: SessionAdmin,
    accessToken: string,
    refreshToken: string
  ): void {
    writeStoredSession({
      admin_id: admin.id,
      email: admin.email,
      name: admin.name,
      access_token: accessToken,
      refresh_token: refreshToken,
    });
  }

  /** Whether there is a session at all. */
  static isSessionValid(): boolean {
    return readStoredSession() !== null;
  }

  /** The session, or nothing where there is not one. */
  static getSession(): AdminSession {
    return readStoredSession();
  }

  /** Who is signed in, as the id the API keys its admin records on. */
  static getAdminId(): number | null {
    return readStoredSession()?.admin_id ?? null;
  }

  /** The token attached to every request — see `lib/api/client.ts`. Reading
   *  it here is only ever for handing to the sign-out call, which needs the
   *  refresh token in its body and the access token is added automatically. */
  static getRefreshToken(): string | null {
    return readStoredSession()?.refresh_token ?? null;
  }

  /** Signing out. Local only — ending the session on the server is a separate
   *  call, made before this one by whatever asked to sign out. */
  static clearSession(): void {
    clearStoredSession();
  }
}

export default AuthUtils;
